import {
  useDeleteBuyersCertificateMutation,
  useGetBuyersCertificateDetailsQuery,
  useUpdateBuyersCertificateMutation,
} from '@api/buyersCert.api';
import { BuyersCertificate } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BlockContainer,
  Button,
  BuyersCertificateCreateProvider,
  BuyersCertificateDetailsHeader,
  BuyersCertificateProductSelect,
  BuyersCertificateSerialNumber,
  CustomTextField,
  LoadingError,
  Title,
  useBuyersCertificateCreateContext,
} from '@components';
import { useAppSelector, useAuth, useSendSms, useToast } from '@hooks';
import { Checkbox, Divider, FormControlLabel, FormGroup, Grid, Stack } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { t } from '@translations';
import { longDate } from '@utils/date.utils';
import { useLocation, useNavigate } from 'react-router-dom';

type BuyersCertificateDetailsBlockProps = {
  title?: string;
  textMessage?: string;
};

type BuyersCertificateDetailsFormProps = {
  textMessage?: string;
  canEditData?: boolean;
};

export const BuyersCertificateDetailsBlock = ({
  title,
  textMessage,
}: BuyersCertificateDetailsBlockProps) => {
  const { isBeremaUser } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const currentPage = useAppSelector(getSpecialPage('buyersCertificateDetailsPage')) as string;
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const { currentData, isError, isLoading, error } = useGetBuyersCertificateDetailsQuery(id || '', {
    skip: !id,
  });

  const isLoadingError = (isError || !id) && currentPage?.includes(pathname);
  const errorMessage = (error as any)?.data?.data;

  if (isLoading) {
    return null;
  }

  return (
    <BuyersCertificateCreateProvider currentData={currentData}>
      <BlockContainer>
        <div className='buyers-certificate-details'>
          <BuyersCertificateDetailsHeader title={title} canEditData={isBeremaUser} />
          {isLoadingError && <LoadingError text={errorMessage} />}
          {!isLoadingError && (
            <>
              {currentData?.data?.dealerName && (
                <Title variant='h3' noDot>
                  {currentData?.data?.dealerName}
                </Title>
              )}
              <Divider sx={{ my: 2 }} />

              <BuyersCertificateDetailsForm canEditData={isBeremaUser} textMessage={textMessage} />
            </>
          )}
        </div>
      </BlockContainer>
    </BuyersCertificateCreateProvider>
  );
};

const BuyersCertificateDetailsForm = ({
  textMessage,
  canEditData,
}: BuyersCertificateDetailsFormProps) => {
  const { form, isEditEnabled, toggleEdit } = useBuyersCertificateCreateContext();
  const { isBeremaUser } = useAuth();
  const [deleteTrigger] = useDeleteBuyersCertificateMutation();
  const dealerBuyersCertificateListPage = useAppSelector(
    getSpecialPage('dealerBuyersCertificateListPage')
  );
  const beremaBuyersCertificateListPage = useAppSelector(
    getSpecialPage('beremaBuyersCertificateListPage')
  );
  const buyersCertificateListPage = isBeremaUser
    ? beremaBuyersCertificateListPage
    : dealerBuyersCertificateListPage;
  const navigate = useNavigate();
  const currentData = form.getValues();
  const { toast } = useToast();
  const { sendSms, createSmsMessage } = useSendSms();
  const isButtonDisabled = !form.formState.isDirty || form.formState.isSubmitting;
  const [trigger] = useUpdateBuyersCertificateMutation();
  const watchedCustomerMobile = form.watch('customerMobile');

  const updateBuyersCertificate = async (data: Pick<BuyersCertificate, 'customerMobile'>) => {
    try {
      const formData = new FormData();

      if (currentData) {
        const updatedData = {
          ...currentData,
          ...data,
        };
        Object.entries(updatedData).forEach(([key, value]) => {
          if (!Array.isArray(value)) {
            formData.append(key, value as string);
          }
        });
        const response = await trigger(formData).unwrap();
        const token = response?.data?.token;

        if (textMessage && watchedCustomerMobile && token && !isEditEnabled) {
          const message = createSmsMessage({ token, textMessage });
          sendSms({ to: watchedCustomerMobile, message: message });
        }

        toggleEdit();

        toast({
          severity: 'success',
          message: t(TextCodes.BuyersCertificate.Block.SuccessfullyUpdated),
        });
        form.reset(
          {},
          {
            keepValues: true,
            keepDirty: false,
            keepDefaultValues: false,
            keepIsSubmitted: false,
            keepTouched: false,
          }
        );
      }
    } catch (error) {
      console.error('Error updating buyers certificate', error);
    }
  };

  const handleSave = () => {
    form.handleSubmit(updateBuyersCertificate)();
  };

  const handleDelete = () => {
    if (currentData?.id) {
      deleteTrigger(currentData.id).then(() => {
        if (buyersCertificateListPage) {
          navigate(buyersCertificateListPage);
        }
      });
    }
  };

  return (
    <form onSubmit={form.handleSubmit(updateBuyersCertificate)}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Title noDot variant='h3'>
            {t(TextCodes.Warranty.Block.ProductInformation)}
          </Title>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <BuyersCertificateProductSelect isEditEnabled={isEditEnabled} />
          </Grid>

          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={6} lg={5}>
              <BuyersCertificateSerialNumber isEditEnabled={isEditEnabled} />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <CustomTextField
                {...form.register('engineNumber')}
                fullWidth
                label={t(TextCodes.Warranty.Block.EngineNo)}
                sx={{ mt: 5 }}
                InputProps={{
                  readOnly: !isEditEnabled,
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={6} lg={5}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.Warranty.Block.SaleDate)}
                sx={{ mt: 5 }}
                value={longDate(currentData?.salesDate) || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.RegistrationDate)}
                sx={{ mt: 5 }}
                value={longDate(currentData?.registrationDate) || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={5}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.Warranty.Block.PurchaseType)}
                sx={{ mt: 5 }}
                value={currentData?.purchaseType || ''}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Title noDot variant='h3'>
            {t(TextCodes.BuyersCertificate.Block.CustomerInformation)}
          </Title>

          <CustomTextField
            {...form.register('customerName')}
            fullWidth
            label={t(TextCodes.Warranty.Block.CustomerName)}
            sx={{ mt: 5 }}
            InputProps={{
              readOnly: !isEditEnabled,
            }}
          />

          <CustomTextField
            {...form.register('customerAddress')}
            fullWidth
            label={t(TextCodes.BuyersCertificate.Block.Address)}
            sx={{ mt: 5 }}
            InputProps={{
              readOnly: !isEditEnabled,
            }}
          />

          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={4}>
              <CustomTextField
                {...form.register('customerPostCode', { required: true })}
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.PostCode)}
                sx={{ mt: 5 }}
                error={!!form.formState.errors.customerPostCode}
                InputProps={{
                  readOnly: !isEditEnabled,
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <CustomTextField
                {...form.register('customerCity', { required: true })}
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.City)}
                sx={{ mt: 5 }}
                error={!!form.formState.errors.customerCity}
                InputProps={{
                  readOnly: !isEditEnabled,
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={{
              xs: 0,
              md: 3,
            }}
          >
            <Grid item xs={12} md={6}>
              <CustomTextField
                {...form.register('customerEmail')}
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.Email)}
                sx={{ mt: 5 }}
                InputProps={{
                  readOnly: !isEditEnabled,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t(TextCodes.BuyersCertificate.Block.Mobile)}
                sx={{ mt: 5 }}
                required
                {...form.register('customerMobile', { required: true })}
              />
            </Grid>
          </Grid>

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              sx={{ width: 'fit-content', mt: 2 }}
              control={<Checkbox checked={currentData?.manualDelivered} />}
              label={t(TextCodes.BuyersCertificate.Block.ManualDelivered)}
            />
            <FormControlLabel
              sx={{ width: 'fit-content', mt: 2 }}
              control={<Checkbox checked={currentData?.howItWorksInstructions} />}
              label={t(TextCodes.BuyersCertificate.Block.HowItWorksInstructions)}
            />
            <FormControlLabel
              sx={{ width: 'fit-content', mt: 2 }}
              control={<Checkbox checked={currentData?.deliveredAndTested} />}
              label={t(TextCodes.BuyersCertificate.Block.DeliveredAndTested)}
            />
            <FormControlLabel
              sx={{ width: 'fit-content', mt: 2 }}
              control={<Checkbox checked={currentData?.consentGiven} />}
              label={t(TextCodes.BuyersCertificate.Block.IsConsentGiven)}
            />
          </FormGroup>
        </Grid>
      </Grid>

      {isEditEnabled ? (
        <Button
          variant='primary'
          busy={form.formState.isSubmitting}
          sx={{ mt: 5 }}
          onClick={handleSave}
          disabled={isButtonDisabled}
        >
          {t(TextCodes.Warranty.Block.Save)}
        </Button>
      ) : (
        <Stack direction='row' spacing={2} sx={{ mt: 5 }}>
          <Button
            type='submit'
            busy={form.formState.isSubmitting}
            variant='primary'
            disabled={isButtonDisabled}
          >
            {t(TextCodes.BuyersCertificate.Block.RequestConsent)}
          </Button>
          {canEditData && (
            <Button variant='primary' busy={form.formState.isSubmitting} onClick={handleDelete}>
              {t(TextCodes.Base.Delete)}
            </Button>
          )}
        </Stack>
      )}
    </form>
  );
};
